import React, { useEffect, useState } from "react";
import { DateTime } from "luxon";
import axiosInstance from "../../lib/axiosInstance";

// SlotPicker shows available slots for a doctor on a given date.
// Props:
// - doctorId (string)
// - branchId (string)
// - date (YYYY-MM-DD string)
// - refreshKey (number) to force immediate reload
// - onSelect({ slotId, startISO, endISO, status }) callback when a slot is chosen
export default function SlotPicker({ doctorId, branchId, date, refreshKey = 0, onSelect }) {
  const [clinicTz, setClinicTz] = useState(process.env.REACT_APP_CLINIC_TZ || "UTC");
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStartISO, setSelectedStartISO] = useState("");

  useEffect(() => {
    let mounted = true;
    axiosInstance
      .get("/config")
      .then((r) => {
        const d = r && r.data ? r.data : r;
        if (mounted && d && d.clinicTz) setClinicTz(d.clinicTz);
      })
      .catch(() => {
        // ignore, use default
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!doctorId || !branchId || !date) {
      setSlots([]);
      setSelectedStartISO("");
      return undefined;
    }

    let mounted = true;
    let firstLoad = true;

    async function loadSlots() {
      if (firstLoad) setLoading(true);
      try {
        const r = await axiosInstance.get("/slots/day-status", {
          params: { doctorId, branchId, date, _ts: Date.now() },
          headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
        });
        if (!mounted) return;
        const d = r && r.data ? r.data : r;
        const list = Array.isArray(d.items) ? d.items : [];
        setSlots(
          list.map((s) => ({
            slotId: s.slotId || s._id || s.id || "",
            startISO: s.startISO || s.start || s.startTime,
            endISO: s.endISO || s.end || s.endTime,
            status: String(s.status || "available").toLowerCase(),
            display: `${DateTime.fromISO(s.startISO || s.start || s.startTime).setZone(clinicTz).toLocaleString(DateTime.TIME_SIMPLE)} - ${DateTime.fromISO(s.endISO || s.end || s.endTime).setZone(clinicTz).toLocaleString(DateTime.TIME_SIMPLE)}`,
          }))
        );
      } catch (err) {
        if (mounted) setSlots([]);
      } finally {
        if (firstLoad && mounted) setLoading(false);
        firstLoad = false;
      }
    }

    loadSlots();
    const intervalId = setInterval(loadSlots, 5000);

    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
  }, [doctorId, branchId, date, clinicTz, refreshKey]);

  if (!date) return <div>Select a date</div>;

  return (
    <div className="slot-picker">
      <h4 style={{ margin: "0 0 8px 0" }}>
        Available slots ({DateTime.fromISO(date, { zone: clinicTz }).toLocaleString(DateTime.DATE_MED)})
      </h4>
      {loading ? (
        <div>Loading slots...</div>
      ) : slots.length === 0 ? (
        <div>No slots</div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {(() => {
            const nowLocal = DateTime.now().setZone(clinicTz);
            const selectedDay = DateTime.fromISO(date, { zone: clinicTz }).toISODate();
            const isToday = selectedDay === nowLocal.toISODate();

            return slots.map((s, i) => {
            const startLocal = s.startISO ? DateTime.fromISO(s.startISO).setZone(clinicTz) : null;
            // Block any slot that has already started (or is earlier) for today's date.
            const isPastTimeSlot = Boolean(isToday && startLocal && startLocal <= nowLocal);
            const isDisabled = s.status !== "available" || isPastTimeSlot;
            const statusLabel = isPastTimeSlot ? "past" : s.status;

            return (
            <li key={s.startISO || `slot-${i}`} style={{ marginBottom: 8 }}>
              <button
                type="button"
                disabled={isDisabled}
                className={[
                  !isDisabled && selectedStartISO === s.startISO ? "selected" : "",
                  (s.status === "blocked" || isPastTimeSlot) ? "slot-blocked" : "",
                  s.status === "booked" ? "slot-booked" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => {
                  if (isDisabled) return;
                  setSelectedStartISO(s.startISO || "");
                  onSelect && onSelect({ slotId: s.slotId, startISO: s.startISO, endISO: s.endISO, status: s.status });
                }}
              >
                {s.display} {statusLabel !== "available" ? `(${statusLabel})` : ""}
              </button>
            </li>
          );
          });
          })()}
        </ul>
      )}
    </div>
  );
}

